// ─────────────────────────────────────────────────────────────
// MappingPane — root component for the Question ⇄ Task mapping UI
//
// Three columns:
//   Left   = Questions  (with draggable answer pills)
//   Center = Active links + "+ Add link" button
//   Right  = Tasks      (drop targets; editable metaKey + unit)
//
// All MUI v6, strict TypeScript.
// ─────────────────────────────────────────────────────────────

import * as React from 'react';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

import type {
  SDM, LinkDef, Task, OID, DragSource, SaveOpts, ItemDef,
} from './types';
import { ROOF_ENHANCEMENT_TASKS, isNonMappablePhase } from './constants';
import { QuestionColumn } from './columns/QuestionColumn';
import { LinksColumn }    from './columns/LinksColumn';
import { TaskColumn }     from './columns/TaskColumn';
import { LinkModal }      from './modal/LinkModal';
import { RawExpressionModal } from './modal/RawExpressionModal';
import { parseFormulaTableExpression, parseDeletionToBranches, parseDeletionTrigger, parseSwitchPlanExpression } from './services/expressionParser';
import { planActionEntries } from './expressions';

export interface MappingPaneProps {
  sdm: SDM;
  onUpdate: (updater: (s: SDM) => SDM) => void;
  /** Initial task template. Defaults to the bundled Roof Enhancement sample. */
  initialTasks?: Task[];
  /**
   * Called after every link create / edit / delete with the up-to-date links + tasks.
   * Used by NaomiStudio to sync SDM and task-template JSON to sessionStorage.
   */
  onLinksChanged?: (links: LinkDef[], tasks: Task[]) => void;
}

interface ModalState {
  source: DragSource | null;
  targetTask: Task | null;
  editing: LinkDef | null;
}

export function MappingPane({
  sdm, onUpdate, initialTasks, onLinksChanged,
}: MappingPaneProps): JSX.Element {
  const tv = sdm.TemplateVersion[0];
  const links = tv.LinkDef ?? [];

  const [tasks, setTasks] = useState<Task[]>(
    () => (initialTasks ?? (ROOF_ENHANCEMENT_TASKS as unknown as Task[])).map(t => ({ ...t })),
  );

  const taskById  = useMemo(() => Object.fromEntries(tasks.map(t => [t.id, t])) as Record<number, Task>, [tasks]);
  const itemByOid = useMemo(() => Object.fromEntries(tv.ItemDef.map(i => [i.OID, i])) as Record<OID, ItemDef>, [tv.ItemDef]);

  // Reconstructed FORMULA links: after a publish + reload the authored formula LinkDef is gone
  // (LinkDefs are never written to the wire SDM), leaving only the derived ItemDef + its MethodDef
  // (the IF/ELSE decision-table expression) + the ItemRef wiring. Reverse-parse that expression
  // back into the builder's branch structure so the formula is EDITABLE again. Only canonical,
  // round-trip-verified formulas are reconstructed; deletion flags / passthroughs / hand-written
  // expressions return null from the parser and are left alone. These are NOT _implicit — they
  // carry full populate.branches and route to the editable authored-link card.
  // Reconstructed SWITCH-PLAN links: a wired derived item (keyed by its own dedicated MetaKey)
  // whose MethodDef is a 2-branch quoted-string plan-UID table (an is_empty guard + one match
  // branch — see parseSwitchPlanExpression / buildSwitchPlanExpression). Reversed back into an
  // editable Switch-Plan card, mirroring reconstructedFormulaLinks/reconstructedRemoveLinks below.
  // Runs BEFORE reconstructedFormulaLinks claims the same itemOID: a switch-plan-shaped table also
  // parses successfully as a generic formula (POINT 3 in SdmCodeGen.switchPlanRoundTrip.test.ts),
  // so priority order (not natural fallthrough) decides which card type wins.
  const reconstructedSwitchPlanLinks = useMemo<LinkDef[]>(() => {
    const methodDefs: any[] = (tv as any).MethodDef ?? [];
    const groups: any[] = (tv as any).ItemGroupDef ?? [];
    if (!methodDefs.length || !groups.length) return [];
    const methodByOID = new Map<string, any>(methodDefs.map((m: any) => [m.OID, m]));

    const wired: Array<{ itemOID: string; methodOID: string }> = [];
    const walk = (gs: any[]): void => gs.forEach((g: any) => {
      (g.ItemRef ?? []).forEach((r: any) => { if (r?.MethodOID && r?.ItemOID) wired.push({ itemOID: r.ItemOID, methodOID: r.MethodOID }); });
      if (g.Subgroups?.length) walk(g.Subgroups);
    });
    walk(groups);
    if (wired.length === 0) return [];

    const tasksByMetaKey = new Map<string, number[]>();
    tasks.forEach(t => {
      const ad = t.additionalData;
      const mk = typeof ad === 'object' && ad ? (ad as { metaKey?: string }).metaKey : undefined;
      if (mk) { const a = tasksByMetaKey.get(mk) ?? []; a.push(t.id); tasksByMetaKey.set(mk, a); }
    });

    const explicitSources = new Set(links.map(l => l.sourceItemOID).filter((o): o is string => !!o));
    const seen = new Set<string>();
    const out: LinkDef[] = [];
    for (const { itemOID, methodOID } of wired) {
      if (explicitSources.has(itemOID) || seen.has(itemOID)) continue;
      const parsed = parseSwitchPlanExpression(methodByOID.get(methodOID)?.FormalExpression?.text);
      if (!parsed) continue; // not an editable Switch-Plan table
      seen.add(itemOID);
      const item = itemByOid[itemOID];
      const mk = item?.MetaKey;
      const targetTaskIds = mk ? (tasksByMetaKey.get(mk) ?? []) : [];
      // Resolve which of the target task's Start/End Automation Plan actions this link owns by
      // finding the FIRST one already flagged resolvePlanFromOwnMetaKey — falling back to index 0
      // (e.g. mid-authoring, before the flag has been stamped/published yet).
      const firstTask = targetTaskIds.length ? taskById[targetTaskIds[0]] : undefined;
      let actionIndex = 0;
      if (firstTask) {
        const entries = planActionEntries(firstTask);
        const flagged = entries.find(e => e.action.resolvePlanFromOwnMetaKey === true);
        actionIndex = flagged ? flagged.index : (entries[0]?.index ?? 0);
      }
      out.push({
        OID: `__switchPlan__${itemOID}`,
        sourceItemOID: parsed.field,          // the trigger QUESTION → drives the modal's trigger picker
        sourceMetaKey: mk,
        targetMetaKey: mk,
        targetTaskIds,
        mode: 'switchPlan',
        trigger: { operator: parsed.operator, values: parsed.values },
        switchPlan: {
          pickedPlanUid: parsed.pickedPlanUid,
          fallbackPlanUid: parsed.fallbackPlanUid,
          actionIndex,
        },
      });
    }
    return out;
  }, [tv, tasks, links, itemByOid, taskById]);

  const reconstructedFormulaLinks = useMemo<LinkDef[]>(() => {
    const methodDefs: any[] = (tv as any).MethodDef ?? [];
    const groups: any[] = (tv as any).ItemGroupDef ?? [];
    if (!methodDefs.length || !groups.length) return [];
    const methodByOID = new Map<string, any>(methodDefs.map((m: any) => [m.OID, m]));

    // Collect every (ItemOID → MethodOID) wiring across all (possibly nested) groups.
    const wired: Array<{ itemOID: string; methodOID: string }> = [];
    const walk = (gs: any[]): void => gs.forEach((g: any) => {
      (g.ItemRef ?? []).forEach((r: any) => { if (r?.MethodOID && r?.ItemOID) wired.push({ itemOID: r.ItemOID, methodOID: r.MethodOID }); });
      if (g.Subgroups?.length) walk(g.Subgroups);
    });
    walk(groups);
    if (wired.length === 0) return [];

    const tasksByMetaKey = new Map<string, number[]>();
    tasks.forEach(t => {
      const ad = t.additionalData;
      const mk = typeof ad === 'object' && ad ? (ad as { metaKey?: string }).metaKey : undefined;
      if (mk) { const a = tasksByMetaKey.get(mk) ?? []; a.push(t.id); tasksByMetaKey.set(mk, a); }
    });

    // Don't duplicate a formula already represented by an authored (in-session) link.
    const explicitSources = new Set(links.map(l => l.sourceItemOID).filter((o): o is string => !!o));
    // A switch-plan-shaped 2-branch quoted-string table also parses successfully as a generic
    // formula (POINT 3, SdmCodeGen.switchPlanRoundTrip.test.ts) — exclude anything
    // reconstructedSwitchPlanLinks already claimed so Switch-Plan cards take priority.
    const switchPlanClaimed = new Set(reconstructedSwitchPlanLinks.map(l => l.OID.replace('__switchPlan__', '')));
    const seen = new Set<string>();
    const out: LinkDef[] = [];
    for (const { itemOID, methodOID } of wired) {
      if (explicitSources.has(itemOID) || seen.has(itemOID) || switchPlanClaimed.has(itemOID)) continue;
      const parsed = parseFormulaTableExpression(methodByOID.get(methodOID)?.FormalExpression?.text);
      if (!parsed) continue; // not an editable decision-table formula
      seen.add(itemOID);
      const item = itemByOid[itemOID];
      const mk = item?.MetaKey;
      const targetTaskIds = mk ? (tasksByMetaKey.get(mk) ?? []) : [];
      // Carry the Unit already stored on the target task(s) back into the link so the editor
      // pre-fills the Unit dropdown (the unit lives on task.additionalData.unit after publish).
      const firstTask = targetTaskIds.length ? taskById[targetTaskIds[0]] : undefined;
      const unitTo = firstTask && typeof firstTask.additionalData === 'object'
        ? (firstTask.additionalData as { unit?: string }).unit
        : undefined;
      out.push({
        OID: `__formula__${itemOID}`,
        sourceItemOID: itemOID,
        sourceMetaKey: mk,
        targetMetaKey: mk,
        targetTaskIds,
        mode: 'formula',
        trigger: { operator: 'always', values: [] },
        populate: {
          field: 'additionalData.quantity',
          expression: '/* decision table */',
          formulaName: (item as any)?.Name || item?.Question?.TranslatedText?.text || itemOID,
          formulaMetaKey: mk,
          formulaVisible: (item as any)?._visible ?? false,
          branches: parsed.branches,
          defaultValue: parsed.defaultValue,
          unitTo,
        },
      });
    }
    return out;
  }, [tv, tasks, links, itemByOid, taskById, reconstructedSwitchPlanLinks]);

  // Reconstructed REMOVE (deletion) links: a wired derived item whose MethodDef is a deletion table
  // (branch values are `true`). After reload these would otherwise show as a non-editable implicit
  // card — or, worse, be mistaken for a formula. Reverse-parse the deletion expression into Remove
  // branches so it shows as an editable Remove card. Round-trip-guarded; non-canonical → skipped.
  const reconstructedRemoveLinks = useMemo<LinkDef[]>(() => {
    const methodDefs: any[] = (tv as any).MethodDef ?? [];
    const groups: any[] = (tv as any).ItemGroupDef ?? [];
    if (!methodDefs.length || !groups.length) return [];
    const methodByOID = new Map<string, any>(methodDefs.map((m: any) => [m.OID, m]));
    const wired: Array<{ itemOID: string; methodOID: string }> = [];
    const walk = (gs: any[]): void => gs.forEach((g: any) => {
      (g.ItemRef ?? []).forEach((r: any) => { if (r?.MethodOID && r?.ItemOID) wired.push({ itemOID: r.ItemOID, methodOID: r.MethodOID }); });
      if (g.Subgroups?.length) walk(g.Subgroups);
    });
    walk(groups);
    if (wired.length === 0) return [];

    const tasksByMetaKey = new Map<string, number[]>();
    tasks.forEach(t => {
      const ad = t.additionalData;
      const mk = typeof ad === 'object' && ad ? (ad as { metaKey?: string }).metaKey : undefined;
      if (mk) { const a = tasksByMetaKey.get(mk) ?? []; a.push(t.id); tasksByMetaKey.set(mk, a); }
    });
    const authoredOIDs = new Set(links.map(l => l.OID));
    const authoredSources = new Set(links.map(l => l.sourceItemOID).filter((o): o is string => !!o));
    const seen = new Set<string>();
    const out: LinkDef[] = [];
    for (const { itemOID, methodOID } of wired) {
      // Skip if this derived item is already an authored link (in-session, incl. one just saved
      // from a reconstructed card — same synthetic OID) or its source is authored.
      if (authoredOIDs.has(`__remove__${itemOID}`) || authoredSources.has(itemOID) || seen.has(itemOID)) continue;
      const text = methodByOID.get(methodOID)?.FormalExpression?.text;
      if (!text) continue;
      const item = itemByOid[itemOID];
      const mk = item?.MetaKey;
      const targetTaskIds = mk ? (tasksByMetaKey.get(mk) ?? []) : [];
      // Prefer the single-trigger recognizer (eq / ne / not_contains / contains / numeric / empty —
      // what the Remove builder actually emits via buildDeletionExpression); fall back to the
      // multi-condition branch parser (condition builder).
      const trig = parseDeletionTrigger(text);
      if (trig) {
        seen.add(itemOID);
        out.push({
          OID: `__remove__${itemOID}`,
          sourceItemOID: trig.field,            // the trigger QUESTION → drives the modal's trigger picker
          targetMetaKey: mk,
          targetTaskIds,
          mode: 'remove',
          trigger: { operator: trig.op, values: trig.values },
          remove: { cascadeDependents: false },
        });
        continue;
      }
      const branches = parseDeletionToBranches(text);
      if (!branches) continue; // not a recognized deletion → handled by the raw-expression fallback
      seen.add(itemOID);
      out.push({
        OID: `__remove__${itemOID}`,
        sourceItemOID: itemOID,
        sourceMetaKey: mk,
        targetMetaKey: mk,
        targetTaskIds,
        mode: 'remove',
        trigger: { operator: 'always', values: [] },
        remove: {
          cascadeDependents: false,
          branches,
          formulaName: (item as any)?.Name || item?.Question?.TranslatedText?.text || itemOID,
          formulaVisible: (item as any)?._visible ?? false,
        },
      });
    }
    return out;
  }, [tv, tasks, links, itemByOid]);

  // Reconstructed RAW-EXPRESSION links: every other wired derived item whose MethodDef the
  // structured recognizers can't model (hand-written JS IIFEs, hybrids, empty-guard deletions,
  // bare arithmetic). Instead of leaving these as non-editable implicit cards, surface them as
  // editable RAW expression cards (edit the FormalExpression text directly). This is the universal
  // fallback that makes EVERY formula editable — structured where possible, raw otherwise.
  const reconstructedRawExpressionLinks = useMemo<LinkDef[]>(() => {
    const methodDefs: any[] = (tv as any).MethodDef ?? [];
    const groups: any[] = (tv as any).ItemGroupDef ?? [];
    if (!methodDefs.length || !groups.length) return [];
    const methodByOID = new Map<string, any>(methodDefs.map((m: any) => [m.OID, m]));
    const wired: Array<{ itemOID: string; methodOID: string }> = [];
    const walk = (gs: any[]): void => gs.forEach((g: any) => {
      (g.ItemRef ?? []).forEach((r: any) => { if (r?.MethodOID && r?.ItemOID) wired.push({ itemOID: r.ItemOID, methodOID: r.MethodOID }); });
      if (g.Subgroups?.length) walk(g.Subgroups);
    });
    walk(groups);
    if (wired.length === 0) return [];

    const tasksByMetaKey = new Map<string, number[]>();
    tasks.forEach(t => {
      const ad = t.additionalData;
      const mk = typeof ad === 'object' && ad ? (ad as { metaKey?: string }).metaKey : undefined;
      if (mk) { const a = tasksByMetaKey.get(mk) ?? []; a.push(t.id); tasksByMetaKey.set(mk, a); }
    });
    // Skip items already authored, or already structurally editable as formula/remove cards.
    // Reconstructed cards are keyed by derived OID in their synthetic OID (`__formula__X` /
    // `__remove__X`) — a trigger-based remove's sourceItemOID is the trigger question, NOT the
    // derived item, so dedup by the OID suffix to cover that case too.
    const covered = new Set<string>([
      ...links.map(l => l.sourceItemOID).filter((o): o is string => !!o),
      ...reconstructedFormulaLinks.map(l => l.OID.replace('__formula__', '')),
      ...reconstructedRemoveLinks.map(l => l.OID.replace('__remove__', '')),
      ...reconstructedSwitchPlanLinks.map(l => l.OID.replace('__switchPlan__', '')),
    ]);

    const seen = new Set<string>();
    const out: LinkDef[] = [];
    for (const { itemOID, methodOID } of wired) {
      if (covered.has(itemOID) || seen.has(itemOID)) continue;
      const text = methodByOID.get(methodOID)?.FormalExpression?.text;
      if (!text) continue;
      if (text.includes('RepeatCount')) continue; // repeating-group count, not a formula
      seen.add(itemOID);
      const item = itemByOid[itemOID];
      const mk = item?.MetaKey;
      out.push({
        OID: `__expr__${itemOID}`,
        sourceItemOID: itemOID,
        sourceMetaKey: mk,
        targetMetaKey: mk,
        targetTaskIds: mk ? (tasksByMetaKey.get(mk) ?? []) : [],
        mode: 'formula',
        trigger: { operator: 'always', values: [] },
        _rawOnly: true,
        _methodOID: methodOID,
        _rawExpression: text,
      });
    }
    return out;
  }, [tv, tasks, links, itemByOid, reconstructedFormulaLinks, reconstructedRemoveLinks, reconstructedSwitchPlanLinks]);

  // Implicit links: a MetaKey present on BOTH a question item and a task IS a link — ASE
  // pairs them by MetaKey. Authored LinkDefs are never written to the wire SDM, so after a
  // publish + reload these MetaKey matches are the only surviving question↔task relationship.
  // Surface them in the Active Links column. Display-only: never stored, never edited.
  const metaKeyLinks = useMemo<LinkDef[]>(() => {
    const tasksByMetaKey = new Map<string, number[]>();
    tasks.forEach(t => {
      const ad = t.additionalData;
      const mk = typeof ad === 'object' && ad ? (ad as { metaKey?: string }).metaKey : undefined;
      if (mk) {
        const ids = tasksByMetaKey.get(mk) ?? [];
        ids.push(t.id);
        tasksByMetaKey.set(mk, ids);
      }
    });
    if (tasksByMetaKey.size === 0) return [];

    // Skip MetaKeys already represented by an authored link OR a reconstructed formula link
    // (avoid showing the same relationship twice — once as an editable formula, once as implicit).
    const explicitMetaKeys = new Set([
      ...links.map(l => l.targetMetaKey),
      ...reconstructedFormulaLinks.map(l => l.targetMetaKey),
      ...reconstructedRemoveLinks.map(l => l.targetMetaKey),
      ...reconstructedRawExpressionLinks.map(l => l.targetMetaKey),
      ...reconstructedSwitchPlanLinks.map(l => l.targetMetaKey),
    ].filter((m): m is string => !!m));
    // The first question item carrying each MetaKey is the question side of the pair.
    const itemByMetaKey = new Map<string, OID>();
    tv.ItemDef.forEach(it => {
      if (it.MetaKey && !itemByMetaKey.has(it.MetaKey)) itemByMetaKey.set(it.MetaKey, it.OID);
    });

    const out: LinkDef[] = [];
    tasksByMetaKey.forEach((taskIds, mk) => {
      if (explicitMetaKeys.has(mk)) return;        // already shown as an authored link
      const sourceItemOID = itemByMetaKey.get(mk);
      if (!sourceItemOID) return;                  // MetaKey only on the task side → not a pair
      out.push({
        OID: `__mk__${mk}`,
        sourceItemOID,
        sourceMetaKey: mk,
        targetMetaKey: mk,
        targetTaskIds: taskIds,
        mode: 'populate',
        trigger: { operator: 'always', values: [] },
        _implicit: true,
      });
    });
    return out;
  }, [tasks, tv.ItemDef, links, reconstructedFormulaLinks, reconstructedRemoveLinks, reconstructedRawExpressionLinks, reconstructedSwitchPlanLinks]);

  // What the three display columns render: authored links + reconstructed (editable) formula AND
  // remove AND switch-plan links + raw-expression (editable-as-text) links + inferred
  // (display-only) MetaKey links.
  const displayLinks = useMemo(
    () => [...links, ...reconstructedFormulaLinks, ...reconstructedRemoveLinks, ...reconstructedSwitchPlanLinks, ...reconstructedRawExpressionLinks, ...metaKeyLinks],
    [links, reconstructedFormulaLinks, reconstructedRemoveLinks, reconstructedSwitchPlanLinks, reconstructedRawExpressionLinks, metaKeyLinks],
  );

  const [selectedOID,    setSelectedOID]    = useState<OID | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [hoveredTaskId,  setHoveredTaskId]  = useState<number | null>(null);
  const [dragSource,     setDragSource]     = useState<DragSource | null>(null);
  const [dropTaskId,     setDropTaskId]     = useState<number | null>(null);
  const [modal,          setModal]          = useState<ModalState | null>(null);
  const [rawModal,       setRawModal]       = useState<{ methodOID: string; label: string; text: string } | null>(null);

  // Notify parent after every link / task / MethodDef-text change so it can sync to sessionStorage.
  // Use a stable string key instead of the links array reference — tv.LinkDef ?? []
  // creates a new array reference every render when LinkDef is undefined, which would
  // fire the effect on every render and overwrite sessionStorage with an empty-links SDM.
  const linksKey = links.map(l => l.OID).join(',');
  // Captures MethodDef expression text so a raw-expression edit (which doesn't change links)
  // still triggers a sync. Live MethodDef text only changes via the raw editor.
  const methodDefsKey = useMemo(
    () => ((tv as any).MethodDef ?? []).map((m: any) => `${m.OID}:${m.FormalExpression?.text ?? ''}`).join('|'),
    [tv],
  );
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    onLinksChanged?.(links, tasks);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linksKey, tasks, methodDefsKey]);

  const onStartDrag = useCallback((p: DragSource) => setDragSource(p), []);
  const onEndDrag   = useCallback(() => { setDragSource(null); setDropTaskId(null); }, []);

  const onDragOverTask = useCallback((taskId: number, e: React.DragEvent) => {
    if (!dragSource) return;
    e.preventDefault();
    // Sales-phase (non-mappable) tasks reject the drop: show a "no-drop" cursor and never highlight.
    const target = taskById[taskId];
    if (target && isNonMappablePhase(target.phase)) {
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
      setDropTaskId(prev => (prev === taskId ? null : prev));
      return;
    }
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'link';
    setDropTaskId(prev => (prev === taskId ? prev : taskId));
  }, [dragSource, taskById]);

  const onDragLeaveTask = useCallback((taskId: number) => {
    setDropTaskId(prev => (prev === taskId ? null : prev));
  }, []);

  const onDrop = useCallback((taskId: number, e: React.DragEvent) => {
    e.preventDefault();
    if (!dragSource) return;
    const target = taskById[taskId];
    // Sales-phase tasks can't be mapped (sales scheduling runs before mapping) — ignore the drop.
    if (target && isNonMappablePhase(target.phase)) {
      setDragSource(null);
      setDropTaskId(null);
      return;
    }
    setModal({ source: dragSource, targetTask: target ?? null, editing: null });
    setDragSource(null);
    setDropTaskId(null);
  }, [dragSource, taskById]);

  /** Update one task's additionalData (metaKey / unit). */
  const updateTask = useCallback((taskId: number, patch: { metaKey?: string | null; unit?: string | null }) => {
    setTasks(arr => arr.map(t => {
      if (t.id !== taskId) return t;
      const ad = (typeof t.additionalData === 'object' && t.additionalData) ? t.additionalData : {};
      const merged: Record<string, unknown> = { ...ad, ...patch };
      const cleaned = Object.fromEntries(
        Object.entries(merged).filter(([, v]) => v !== null && v !== undefined && v !== ''),
      );
      return {
        ...t,
        additionalData: (Object.keys(cleaned).length ? cleaned : '') as Task['additionalData'],
      };
    }));
  }, []);

  /** Persist a created/updated link. Handles MetaKey stamping + derived-item creation. */
  const saveLink = useCallback((link: LinkDef, opts: SaveOpts = {}) => {
    onUpdate(s => {
      const v = s.TemplateVersion[0];
      const existing = (v.LinkDef ?? []).findIndex(l => l.OID === link.OID);
      const nextLinks = existing >= 0
        ? (v.LinkDef ?? []).map((l, i) => (i === existing ? link : l))
        : [...(v.LinkDef ?? []), link];

      let nextItems = v.ItemDef;

      // Stamp MetaKey on the source question when it has none
      if (opts.stampMetaKey && opts.stampMetaKeyValue) {
        nextItems = nextItems.map(it =>
          it.OID === link.sourceItemOID && !it.MetaKey
            ? { ...it, MetaKey: opts.stampMetaKeyValue ?? undefined }
            : it,
        );
      }

      // Create (or update, on edit) the derived item for formula-mode links
      if (opts.createDerivedItem?.name) {
        const cd = opts.createDerivedItem;
        // A derived/computed item is one wired to a MethodDef via an ItemGroupDef ItemRef.
        // (The _derived flag does NOT survive the wire round-trip, so identify reloaded ones
        //  by their MethodDef wiring too — otherwise editing a reconstructed formula would mint
        //  a duplicate derived item and orphan the original.)
        const computedOIDs = new Set<string>();
        const collect = (gs: any[]): void => (gs ?? []).forEach((g: any) => {
          (g.ItemRef ?? []).forEach((r: any) => { if (r?.MethodOID && r?.ItemOID) computedOIDs.add(r.ItemOID); });
          if (g.Subgroups?.length) collect(g.Subgroups);
        });
        collect((v as any).ItemGroupDef ?? []);
        // Reuse the derived item this link already points at (edit of an authored OR a reconstructed
        // formula), else the one a prior authored link owned, else mint a new OID (fresh formula).
        const prevLink = existing >= 0 ? (v.LinkDef ?? [])[existing] : undefined;
        const ownByLink = link.sourceItemOID
          ? nextItems.find(it => it.OID === link.sourceItemOID && (it._derived || computedOIDs.has(it.OID)))
          : undefined;
        const prevDerived = ownByLink ?? (prevLink?.sourceItemOID
          ? nextItems.find(it => it.OID === prevLink.sourceItemOID && it._derived)
          : undefined);
        const derivedOID = prevDerived?.OID
          ?? `I.${Math.max(0, ...nextItems.map(i => parseInt(i.OID.slice(2), 10) || 0)) + 1}`;
        const newItem: ItemDef = {
          OID: derivedOID,
          Name: cd.name.replace(/\s+/g, ''),
          DataType: 'integer',
          Question: { TranslatedText: { lang: 'en', text: cd.name + ' (Derived)' } },
          Description: 'Auto-generated by NAOMI Mapping — formula link',
          ...(cd.metaKey ? { MetaKey: cd.metaKey } : {}),
          _derived: true,
          _visible: cd.visible,
        };
        nextItems = prevDerived
          ? nextItems.map(it => (it.OID === derivedOID ? newItem : it))
          : [...nextItems, newItem];
        // Rebind link to the derived item
        const replaced = nextLinks.findIndex(l => l.OID === link.OID);
        if (replaced >= 0) {
          nextLinks[replaced] = {
            ...nextLinks[replaced],
            sourceItemOID: derivedOID,
            sourceMetaKey: cd.metaKey ?? nextLinks[replaced].sourceMetaKey,
          };
        }
      }

      // Remove links: create the derived deletion-flag ItemDef in the live SDM so it shows as a
      // "(Derived) …" question immediately (no publish/reload). Same shape applyRemoveLinkToSdm
      // produces, so publish reuses it by MetaKey (no duplicate). No source rebind — the remove
      // link's source stays its trigger question. Idempotent: skip if an item with this MetaKey
      // already exists (edit/re-save, or a reloaded+reconstructed remove already carries it).
      if (opts.createRemoveDerivedItem?.metaKey) {
        const rmk = opts.createRemoveDerivedItem.metaKey;
        // Editing a remove link's MetaKey (e.g. Foo → Bar): drop the now-orphaned derived item
        // keyed by the OLD MetaKey before adding the new one — unless another remaining link still
        // uses it. Mirrors deleteLink's authoredRemoveMK cleanup; without this the old derived item
        // (Foo) would be left behind permanently once the link points at Bar.
        const prevRemoveMK = existing >= 0 ? (v.LinkDef ?? [])[existing]?.targetMetaKey : undefined;
        if (prevRemoveMK && prevRemoveMK !== rmk
            && !nextLinks.some(l => l.targetMetaKey === prevRemoveMK)) {
          nextItems = nextItems.filter(it => !(it.MetaKey === prevRemoveMK && it._derived === true));
        }
        if (!nextItems.some(it => it.MetaKey === rmk)) {
          const derivedOID = `I.${Math.max(0, ...nextItems.map(i => parseInt(i.OID.slice(2), 10) || 0)) + 1}`;
          nextItems = [...nextItems, {
            OID: derivedOID,
            Name: rmk,
            DataType: 'integer',
            Question: { TranslatedText: { lang: 'en', text: `(Derived) ${rmk}` } },
            Description: 'Auto-generated by NAOMI Mapping — remove link',
            MetaKey: rmk,
            _derived: true,
          }];
        }
      }

      return {
        ...s,
        TemplateVersion: s.TemplateVersion.map((vv, i) => i === 0
          ? { ...vv, LinkDef: nextLinks, ItemDef: nextItems }
          : vv,
        ),
      };
    });

    // Stamp MetaKey (if the task has none) and/or the user-entered Unit on the target tasks.
    if (opts.stampTaskIds && (opts.stampTaskMetaKey || opts.stampTaskUnit)) {
      const ids = new Set(opts.stampTaskIds);
      setTasks(arr => arr.map(t => {
        if (!ids.has(t.id)) return t;
        const ad = (typeof t.additionalData === 'object' && t.additionalData) ? t.additionalData : {};
        const next: Record<string, unknown> = { ...ad };
        // MetaKey: only fill when empty (don't clobber an established key).
        if (opts.stampTaskMetaKey && opts.stampTaskMetaKeyValue && !next.metaKey) {
          next.metaKey = opts.stampTaskMetaKeyValue;
        }
        // Unit: the author explicitly entered it for this populate link — apply it.
        if (opts.stampTaskUnit && opts.stampTaskUnitValue) {
          next.unit = opts.stampTaskUnitValue;
        }
        return { ...t, additionalData: next };
      }));
    }

    // Switch-Plan links: stamp resolvePlanFromOwnMetaKey:true on the target task's chosen action
    // immediately (live SDM), mirroring what applySwitchPlanLinksToTasks does at sync/publish time
    // — so the flag is visible right after authoring, same as every other "stamp X now" opt above.
    if (opts.stampActionPlanResolution) {
      const { taskId: spTaskId, actionIndex } = opts.stampActionPlanResolution;
      setTasks(arr => arr.map(t => {
        if (t.id !== spTaskId) return t;
        const raw: Record<string, unknown> = (t._raw && typeof t._raw === 'object') ? t._raw : {};
        const actions: Array<Record<string, unknown>> = Array.isArray(raw.actions)
          ? raw.actions as Array<Record<string, unknown>>
          : [];
        const nextActions = actions.map((a, i) => (i === actionIndex ? { ...a, resolvePlanFromOwnMetaKey: true } : a));
        return { ...t, _raw: { ...raw, actions: nextActions } };
      }));
    }

    setModal(null);
  }, [onUpdate]);

  const deleteLink = useCallback((oid: OID) => {
    // Reconstructed formula/remove/switch-plan links + inferred MetaKey links aren't in v.LinkDef
    // (synthetic OID) — resolve from all sets.
    const dying = links.find(l => l.OID === oid)
      ?? reconstructedFormulaLinks.find(l => l.OID === oid)
      ?? reconstructedRemoveLinks.find(l => l.OID === oid)
      ?? reconstructedSwitchPlanLinks.find(l => l.OID === oid)
      ?? metaKeyLinks.find(l => l.OID === oid);

    onUpdate(s => {
      const v = s.TemplateVersion[0];
      const remaining = (v.LinkDef ?? []).filter(l => l.OID !== oid);

      let nextItems = v.ItemDef;
      const groupsAll: any[] = (v as any).ItemGroupDef ?? [];
      // Identify the derived item to strip. Reconstructed cards encode it in the synthetic OID
      // (`__formula__X` / `__remove__X` / `__switchPlan__X`) — robust even for a trigger-based
      // remove/switch-plan whose sourceItemOID is the trigger QUESTION, not the derived flag.
      // Authored formula links carry it on sourceItemOID (rebound to the derived OID at save).
      const dyingDerivedOID = oid.startsWith('__formula__') ? oid.slice('__formula__'.length)
        : oid.startsWith('__remove__') ? oid.slice('__remove__'.length)
        : oid.startsWith('__switchPlan__') ? oid.slice('__switchPlan__'.length)
        : (dying?.mode === 'formula' ? dying.sourceItemOID : undefined);

      // Authored (in-session) remove link: its derived deletion-flag item was created live as an
      // UNWIRED ItemDef keyed by targetMetaKey (no MethodDef/ItemRef yet — those come at publish).
      // dyingDerivedOID doesn't cover it (source is the trigger question, OID isn't __remove__), so
      // strip it here by MetaKey when no remaining authored link still uses that MetaKey.
      const authoredRemoveMK = (!dyingDerivedOID && dying?.mode === 'remove'
        && !oid.startsWith('__remove__') && dying?.targetMetaKey) ? dying.targetMetaKey : undefined;
      if (authoredRemoveMK) {
        const stillUsed = remaining.some(l => l.targetMetaKey === authoredRemoveMK);
        const nextItemsRm = stillUsed
          ? v.ItemDef
          : v.ItemDef.filter(it => !(it.MetaKey === authoredRemoveMK && (it as any)._derived === true));
        return {
          ...s,
          TemplateVersion: s.TemplateVersion.map((vv, i) => i === 0
            ? { ...vv, LinkDef: remaining, ItemDef: nextItemsRm }
            : vv,
          ),
        };
      }

      if (dyingDerivedOID) {
        // The derived item's MethodDef + ItemRef are real persisted artifacts (formula or deletion)
        // — strip all three so nothing is orphaned.
        const groups: any[] = groupsAll;
        const methodOIDs = new Set<string>();
        groups.forEach((g: any) => (g.ItemRef ?? []).forEach((r: any) => {
          if (r?.ItemOID === dyingDerivedOID && r?.MethodOID) methodOIDs.add(r.MethodOID);
        }));
        nextItems = nextItems.filter(it => it.OID !== dyingDerivedOID);
        const nextMethods = ((v as any).MethodDef ?? []).filter((m: any) => !methodOIDs.has(m.OID));
        const nextGroups = groups.map((g: any) => ({
          ...g,
          ItemRef: (g.ItemRef ?? []).filter((r: any) => r.ItemOID !== dyingDerivedOID),
        }));
        return {
          ...s,
          TemplateVersion: s.TemplateVersion.map((vv, i) => i === 0
            ? { ...vv, LinkDef: remaining, ItemDef: nextItems, MethodDef: nextMethods, ItemGroupDef: nextGroups } as any
            : vv,
          ),
        };
      } else if (dying?.sourceItemOID) {
        // Clear MetaKey from source question if no remaining link still uses it
        const stillReferenced = remaining.some(l => l.sourceItemOID === dying.sourceItemOID);
        if (!stillReferenced) {
          nextItems = nextItems.map(it =>
            it.OID === dying.sourceItemOID ? { ...it, MetaKey: undefined } : it,
          );
        }
      }

      return {
        ...s,
        TemplateVersion: s.TemplateVersion.map((vv, i) => i === 0
          ? { ...vv, LinkDef: remaining, ItemDef: nextItems }
          : vv,
        ),
      };
    });

    // Clear MetaKey from target tasks if no remaining link still targets them
    if (dying?.targetTaskIds?.length) {
      const remaining = links.filter(l => l.OID !== oid);
      setTasks(arr => arr.map(t => {
        if (!dying.targetTaskIds.includes(t.id)) return t;
        const stillReferenced = remaining.some(l => l.targetTaskIds.includes(t.id));
        if (stillReferenced) return t;
        const ad = typeof t.additionalData === 'object' && t.additionalData
          ? t.additionalData as Record<string, unknown>
          : {};
        const rest: Record<string, unknown> = { ...ad };
        delete rest.metaKey;
        // An implicit MetaKey link binds via the shared metaKey (+ the populate unit on the task).
        // Deleting it removes the binding, so drop the now-orphaned unit as well.
        if (oid.startsWith('__mk__')) delete rest.unit;
        let nextRaw = t._raw;
        // Deleting a Switch-Plan link must also clear the resolvePlanFromOwnMetaKey opt-in it
        // stamped on the task's action (the save path above, line ~610), otherwise the task is left
        // with the flag ON but no MetaKey left to resolve from. Per work item #29812, tenant-web's
        // applyActionPlanResolution reads exactly this flag to decide whether to override
        // automationPlanMasterUid, so this stale combination could be misread downstream.
        if (dying.mode === 'switchPlan' && dying.switchPlan) {
          const raw: Record<string, unknown> = (t._raw && typeof t._raw === 'object') ? t._raw : {};
          const actions: Array<Record<string, unknown>> = Array.isArray(raw.actions)
            ? raw.actions as Array<Record<string, unknown>>
            : [];
          const { actionIndex } = dying.switchPlan;
          nextRaw = {
            ...raw,
            actions: actions.map((a, i) => (i === actionIndex ? { ...a, resolvePlanFromOwnMetaKey: false } : a)),
          };
        }
        return { ...t, additionalData: Object.keys(rest).length ? rest : '', _raw: nextRaw };
      }));
    }
  }, [onUpdate, links, reconstructedFormulaLinks, reconstructedRemoveLinks, reconstructedSwitchPlanLinks, metaKeyLinks]);

  const editLink = useCallback((l: LinkDef) => {
    // Raw-only cards open the raw expression editor instead of the structured link modal.
    if (l._rawOnly && l._methodOID) {
      const item = l.sourceItemOID ? itemByOid[l.sourceItemOID] : undefined;
      const label = (item as any)?.Name || item?.Question?.TranslatedText?.text || l.targetMetaKey || l._methodOID;
      setRawModal({ methodOID: l._methodOID, label, text: l._rawExpression ?? '' });
      return;
    }
    const target = taskById[l.targetTaskIds[0]] ?? null;
    setModal({
      source: l.sourceItemOID
        ? { itemOID: l.sourceItemOID, value: (l.trigger?.values?.[0] ?? null) as DragSource['value'], generic: !l.trigger?.values?.length }
        : null,
      targetTask: target,
      editing: l,
    });
  }, [taskById, itemByOid]);

  /** Save a raw FormalExpression edit straight into its MethodDef (by OID), in place. */
  const saveExpression = useCallback((methodOID: string, text: string) => {
    onUpdate(s => {
      const methods = (((s.TemplateVersion[0] as any).MethodDef) ?? []).map((m: any) =>
        m.OID === methodOID
          ? { ...m, FormalExpression: { Context: m.FormalExpression?.Context ?? 'OpenEDC', text } }
          : m);
      return {
        ...s,
        TemplateVersion: s.TemplateVersion.map((vv, i) => i === 0 ? { ...vv, MethodDef: methods } as any : vv),
      };
    });
    setRawModal(null);
  }, [onUpdate]);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '340px 1fr 340px',
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <QuestionColumn
        sdm={sdm}
        links={displayLinks}
        selectedOID={selectedOID}
        hoveredTaskId={hoveredTaskId}
        onSelect={setSelectedOID}
        onStartDrag={onStartDrag}
        onEndDrag={onEndDrag}
      />

      <LinksColumn
        sdm={sdm}
        links={displayLinks}
        selectedOID={selectedOID}
        hoveredTaskId={hoveredTaskId}
        taskById={taskById}
        itemByOid={itemByOid}
        onEdit={editLink}
        onDelete={deleteLink}
        onAddLink={() => setModal({ source: null, targetTask: null, editing: null })}
        onShowAll={() => setSelectedOID(null)}
      />

      <TaskColumn
        tasks={tasks}
        links={displayLinks}
        selectedOID={selectedOID}
        selectedTaskId={selectedTaskId}
        onSelectTask={setSelectedTaskId}
        onHoverTask={setHoveredTaskId}
        onUnhoverTask={() => setHoveredTaskId(null)}
        dragSource={dragSource}
        dropTaskId={dropTaskId}
        onDragOverTask={onDragOverTask}
        onDragLeaveTask={onDragLeaveTask}
        onDrop={onDrop}
        onUpdateTask={updateTask}
      />

      <LinkModal
        key={modal ? (modal.editing?.OID ?? modal.targetTask?.id ?? 'add') : 'closed'}
        open={!!modal}
        onClose={() => setModal(null)}
        onSave={saveLink}
        sdm={sdm}
        tasks={tasks}
        taskById={taskById}
        source={modal?.source ?? null}
        targetTask={modal?.targetTask ?? null}
        editing={modal?.editing ?? null}
        existingLinks={displayLinks}
      />

      <RawExpressionModal
        open={!!rawModal}
        onClose={() => setRawModal(null)}
        onSave={saveExpression}
        methodOID={rawModal?.methodOID ?? null}
        label={rawModal?.label}
        initialText={rawModal?.text ?? ''}
      />
    </Box>
  );
}
